/**
 * Working out what a tank of fuel actually cost a job.
 *
 * A technician fills the tank on Monday and works until Thursday. The receipt
 * is one number for four days and eleven jobs, and what the business needs to
 * know is what the Thursday job cost. Left to a claim form, that becomes a
 * technician dividing in their head — and the two failure modes are the same
 * number every week, which is invented, or a different method every week, which
 * cannot be added up.
 *
 * So the split is arithmetic here rather than judgement there. The technician
 * supplies what they can actually observe — litres, price, odometer, which days
 * they worked — and the apportioning is done the same way every time.
 *
 * **This produces an estimate and says so.** A tank is apportioned across days
 * or distance; it is not a receipt for one job. What makes it defensible to an
 * auditor is that the inputs are recorded, the method is fixed, and the result
 * is labelled as an approximation rather than presented as a measurement.
 */
/** Sanity bounds. Wide on purpose — these catch typos, not overspending. */
const MAX_LITRES = 200;
const MAX_PRICE_PER_LITRE = 1_000;
const MAX_DAYS = 31;
const MAX_DISTANCE_KM = 5_000;
/**
 * Consumption outside this band is almost always a mistyped odometer rather
 * than a thirsty engine. A small pickup or a motorbike sits well inside it.
 */
const IMPLAUSIBLE_LOW_L_PER_100KM = 2;
const IMPLAUSIBLE_HIGH_L_PER_100KM = 30;
const round2 = (value) => Math.round(value * 100) / 100;
/** Everything wrong with the claim, not just the first thing. */
export function fuelProblems(purchase) {
    const problems = [];
    const { litres, pricePerLitre, daysCovered, odometerStart, odometerEnd, jobsCovered } = purchase;
    if (!Number.isFinite(litres) || litres <= 0) {
        problems.push('How many litres went in?');
    }
    else if (litres > MAX_LITRES) {
        problems.push(`${litres} litres in one fill looks like a typo.`);
    }
    if (!Number.isFinite(pricePerLitre) || pricePerLitre <= 0) {
        problems.push('What was the price per litre?');
    }
    else if (pricePerLitre > MAX_PRICE_PER_LITRE) {
        problems.push(`KES ${pricePerLitre} per litre looks like a typo.`);
    }
    if (!Number.isInteger(daysCovered) || daysCovered < 1) {
        problems.push('How many working days did this tank cover? At least one.');
    }
    else if (daysCovered > MAX_DAYS) {
        problems.push('A tank covering more than a month should be claimed in parts.');
    }
    if (jobsCovered !== null && jobsCovered !== undefined) {
        if (!Number.isInteger(jobsCovered) || jobsCovered < 0) {
            problems.push('The number of jobs has to be a whole number.');
        }
    }
    // Odometer readings are optional, but half a pair tells you nothing.
    const hasStart = odometerStart !== null && odometerStart !== undefined;
    const hasEnd = odometerEnd !== null && odometerEnd !== undefined;
    if (hasStart !== hasEnd) {
        problems.push('Give both odometer readings or neither — one on its own says nothing.');
    }
    else if (hasStart && hasEnd) {
        if (!Number.isFinite(odometerStart) || !Number.isFinite(odometerEnd)) {
            problems.push('The odometer readings have to be numbers.');
        }
        else if (odometerEnd < odometerStart) {
            problems.push('The closing odometer is lower than the opening one.');
        }
        else if (odometerEnd - odometerStart > MAX_DISTANCE_KM) {
            problems.push(`${Math.round(odometerEnd - odometerStart)} km on one tank looks like a typo.`);
        }
    }
    return problems;
}
/**
 * Split a tank across the days, and the distance, that it covered.
 *
 * Throws if the input has not been checked. Call `fuelProblems` first — the
 * caller has to show those messages anyway, and an estimate computed from
 * nonsense is worse than a refusal.
 */
export function estimateFuel(purchase) {
    const problems = fuelProblems(purchase);
    if (problems.length > 0) {
        throw new Error(`Cannot estimate from an invalid claim: ${problems.join(' ')}`);
    }
    const { litres, pricePerLitre, daysCovered, odometerStart, odometerEnd, jobsCovered } = purchase;
    const total = round2(litres * pricePerLitre);
    const perDay = round2(total / daysCovered);
    const perJob = jobsCovered && jobsCovered > 0 ? round2(total / jobsCovered) : null;
    const hasOdometer = odometerStart !== null &&
        odometerStart !== undefined &&
        odometerEnd !== null &&
        odometerEnd !== undefined;
    const distanceKm = hasOdometer ? round2(odometerEnd - odometerStart) : null;
    const consumptionPer100Km = distanceKm && distanceKm > 0 ? round2((litres / distanceKm) * 100) : null;
    const costPerKm = distanceKm && distanceKm > 0 ? round2(total / distanceKm) : null;
    const warnings = [];
    if (consumptionPer100Km !== null) {
        if (consumptionPer100Km < IMPLAUSIBLE_LOW_L_PER_100KM) {
            warnings.push(`${consumptionPer100Km} litres per 100 km is better than any vehicle manages. ` +
                'Check the odometer readings.');
        }
        else if (consumptionPer100Km > IMPLAUSIBLE_HIGH_L_PER_100KM) {
            warnings.push(`${consumptionPer100Km} litres per 100 km is very heavy. Either the distance is ` +
                'understated or the vehicle needs looking at.');
        }
    }
    // A tank claimed against no jobs is not fraud, but it is the thing an
    // approver should ask about before paying it.
    if ((jobsCovered ?? 0) === 0) {
        warnings.push('No jobs were attended on this tank. Say what the travel was for.');
    }
    if (distanceKm === null) {
        warnings.push('No odometer readings, so this is split by day only. Distance is what makes a fuel ' +
            'claim checkable.');
    }
    const basis = distanceKm
        ? `${litres} L at KES ${pricePerLitre}/L over ${distanceKm} km and ` +
            `${daysCovered} working ${daysCovered === 1 ? 'day' : 'days'}`
        : `${litres} L at KES ${pricePerLitre}/L over ${daysCovered} working ` +
            `${daysCovered === 1 ? 'day' : 'days'}`;
    return {
        total,
        perDay,
        perJob,
        distanceKm,
        consumptionPer100Km,
        costPerKm,
        basis,
        warnings,
    };
}
/**
 * What this claim is worth for one day of it.
 *
 * Used when a tank spans days that fall either side of a reporting boundary:
 * the report for August should carry the August days, not the whole tank
 * because it happened to be bought on the 30th.
 */
export function fuelForDays(purchase, days) {
    if (!Number.isInteger(days) || days < 0)
        return 0;
    const estimate = estimateFuel(purchase);
    return round2(estimate.perDay * Math.min(days, purchase.daysCovered));
}
//# sourceMappingURL=fuel.js.map